import React, { useEffect, useState } from "react"

import { format } from "date-fns"
import { Calendar } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Calendar as CalendarComponent } from "@/components/ui/calendar"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import type { ApplicationDetails } from "@/hooks/useApplicationDetails"
import { useUpdateApplicant } from "@/hooks/useUpdateApplicant"

import type { TablesUpdate } from "../../../database.types"
import { isAtOrBefore } from "../../../shared/application-stages.ts"

interface EditApplicantDialogProps {
  isOpen: boolean
  onClose: () => void
  application: ApplicationDetails
}

const EditApplicantDialog = ({ isOpen, onClose, application }: EditApplicantDialogProps) => {
  const updateApplicant = useUpdateApplicant(application.id)
  const [formData, setFormData] = useState<TablesUpdate<"applicants">>({})
  const [activeTab, setActiveTab] = useState("personal")

  useEffect(() => {
    if (isOpen && application?.applicant) {
      setFormData({ ...application.applicant })
      setActiveTab("personal")
    }
  }, [isOpen, application])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleCheckboxChange = (field: string, checked: boolean) => {
    setFormData((prev) => ({ ...prev, [field]: checked }))
  }

  const handleSelectChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleDateChange = (date: Date | undefined) => {
    setFormData((prev) => ({
      ...prev,
      date_of_birth: date ? format(date, "yyyy-MM-dd") : null,
    }))
  }

  // Mirrors ApplicantService.isOwnedByPerson
  const personOwnsDetails =
    Boolean(application.person?.person_id) &&
    !isAtOrBefore(application.current_stage ?? "", "personal-details")

  const handleSave = () => {
    updateApplicant.mutate(
      {
        id: application.applicant?.id,
        data: {
          ...formData,
          updated_at: new Date().toISOString(),
        },
      },
      { onSuccess: onClose },
    )
  }

  const daysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"]
  const companyTypes = ["sole-trader", "limited-company", "partnership", "llp"]
  const selectedDob = formData.date_of_birth
    ? /^(\d{4})-(\d{2})-(\d{2})$/.test(formData.date_of_birth)
      ? new Date(
          Number(formData.date_of_birth.slice(0, 4)),
          Number(formData.date_of_birth.slice(5, 7)) - 1,
          Number(formData.date_of_birth.slice(8, 10)),
        )
      : new Date(formData.date_of_birth)
    : undefined
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Applicant Details</DialogTitle>
        </DialogHeader>

        {personOwnsDetails && (
          <p className="text-sm text-muted-foreground">
            Changes to personal, banking, next-of-kin, tax and address details will not be saved on
            this driver's person record — edit them in people tab.
          </p>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid grid-cols-5 mb-4">
            <TabsTrigger value="personal">Personal</TabsTrigger>
            <TabsTrigger value="banking">Banking</TabsTrigger>
            <TabsTrigger value="kin">Next of Kin</TabsTrigger>
            <TabsTrigger value="tax">Tax & Company</TabsTrigger>
            <TabsTrigger value="address">Address</TabsTrigger>
          </TabsList>

          <TabsContent value="personal" className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">First Name</Label>
                <Input
                  id="first_name"
                  name="first_name"
                  value={formData.first_name || ""}
                  onChange={handleChange}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="last_name">Last Name</Label>
                <Input
                  id="last_name"
                  name="last_name"
                  value={formData.last_name || ""}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="date_of_birth">Date of Birth</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal">
                    <Calendar className="mr-2 h-4 w-4" />
                    {selectedDob ? format(selectedDob, "PPP") : <span>Pick a date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <CalendarComponent
                    mode="single"
                    selected={selectedDob}
                    onSelect={handleDateChange}
                    weekStartsOn={1}
                    captionLayout="dropdown"
                    fromYear={1900}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email || ""}
                onChange={handleChange}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone_number">Phone Number</Label>
              <Input
                id="phone_number"
                name="phone_number"
                value={formData.phone_number || ""}
                onChange={handleChange}
              />
            </div>

            <div className="flex items-center space-x-2 pt-2">
              <Checkbox
                id="has_electric_van"
                checked={formData.has_electric_van || false}
                onCheckedChange={(checked) =>
                  handleCheckboxChange("has_electric_van", checked as boolean)
                }
              />
              <Label htmlFor="has_electric_van">Do you have your own electric van?</Label>
            </div>

            <div className="flex items-center space-x-2 pt-2">
              <Checkbox
                id="needs_rental_van"
                checked={formData.needs_rental_van || false}
                onCheckedChange={(checked) =>
                  handleCheckboxChange("needs_rental_van", checked as boolean)
                }
              />
              <Label htmlFor="needs_rental_van">Do you require a rental van?</Label>
            </div>

            <div className="space-y-2">
              <Label htmlFor="availability_days_count">
                How many days per week are you looking to work?
              </Label>
              <Select
                value={formData.availability_days_count || ""}
                onValueChange={(value) => handleSelectChange("availability_days_count", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select days per week" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0-1">0-1</SelectItem>
                  <SelectItem value="1-3">1-3</SelectItem>
                  <SelectItem value="3-5">3-5</SelectItem>
                  <SelectItem value="5+">5+</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Which days of the week are you looking to work?</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                {daysOfWeek.map((day) => {
                  const dayCode = day.substring(0, 3).toLowerCase()
                  return (
                    <div key={day} className="flex items-center space-x-2">
                      <Checkbox
                        id={`workDay-${day}`}
                        checked={(formData.availability_days || []).includes(dayCode)}
                        onCheckedChange={(checked) => {
                          setFormData((prev) => {
                            const currentDays = [...(prev.availability_days || [])]
                            if (checked && !currentDays.includes(dayCode)) {
                              currentDays.push(dayCode)
                            } else if (!checked) {
                              const index = currentDays.indexOf(dayCode)
                              if (index > -1) currentDays.splice(index, 1)
                            }
                            return { ...prev, availability_days: currentDays }
                          })
                        }}
                      />
                      <Label htmlFor={`workDay-${day}`}>{day}</Label>
                    </div>
                  )
                })}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="banking" className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="bank_name">Bank Name</Label>
              <Input
                id="bank_name"
                name="bank_name"
                value={formData.bank_name || ""}
                onChange={handleChange}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="account_holder_name">Name on Account</Label>
              <Input
                id="account_holder_name"
                name="account_holder_name"
                value={formData.account_holder_name || ""}
                onChange={handleChange}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="account_number">Account Number</Label>
              <Input
                id="account_number"
                name="account_number"
                value={formData.account_number || ""}
                onChange={handleChange}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="sort_code">Sort Code</Label>
              <Input
                id="sort_code"
                name="sort_code"
                value={formData.sort_code || ""}
                onChange={handleChange}
              />
            </div>
          </TabsContent>

          <TabsContent value="kin" className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="next_of_kin_name">Name</Label>
              <Input
                id="next_of_kin_name"
                name="next_of_kin_name"
                value={formData.next_of_kin_name || ""}
                onChange={handleChange}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="next_of_kin_phone">Phone Number</Label>
              <Input
                id="next_of_kin_phone"
                name="next_of_kin_phone"
                value={formData.next_of_kin_phone || ""}
                onChange={handleChange}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="next_of_kin_relation">Relationship</Label>
              <Input
                id="next_of_kin_relation"
                name="next_of_kin_relation"
                value={formData.next_of_kin_relation || ""}
                onChange={handleChange}
              />
            </div>
          </TabsContent>

          <TabsContent value="tax" className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="ni_number">National Insurance Number</Label>
              <Input
                id="ni_number"
                name="ni_number"
                value={formData.ni_number || ""}
                onChange={handleChange}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="utr_number">UTR Number</Label>
              <Input
                id="utr_number"
                name="utr_number"
                value={formData.utr_number || ""}
                onChange={handleChange}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="company_type">Company Type</Label>
              <Select
                value={formData.company_type || ""}
                onValueChange={(value) => handleSelectChange("company_type", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select company type" />
                </SelectTrigger>
                <SelectContent>
                  {companyTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="company_number">Company Number</Label>
              <Input
                id="company_number"
                name="company_number"
                value={formData.company_number || ""}
                onChange={handleChange}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="vat_number">VAT Number</Label>
              <Input
                id="vat_number"
                name="vat_number"
                value={formData.vat_number || ""}
                onChange={handleChange}
              />
            </div>
          </TabsContent>

          <TabsContent value="address" className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="address_line_1">Address Line 1</Label>
              <Input
                id="address_line_1"
                name="address_line_1"
                value={formData.address_line_1 || ""}
                onChange={handleChange}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="address_line_2">Address Line 2</Label>
              <Input
                id="address_line_2"
                name="address_line_2"
                value={formData.address_line_2 || ""}
                onChange={handleChange}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="city">City</Label>
              <Input id="city" name="city" value={formData.city || ""} onChange={handleChange} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="postcode">Postcode</Label>
              <Input
                id="postcode"
                name="postcode"
                value={formData.postcode || ""}
                onChange={handleChange}
              />
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={onClose} disabled={updateApplicant.isPending}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={updateApplicant.isPending}>
            {updateApplicant.isPending ? "Saving..." : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default EditApplicantDialog
